import { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, CheckCheck, Eye, ListOrdered, PenLine, SearchCheck, UserCheck } from 'lucide-react';
import { SectionHeader } from '../SectionHeader';
import { cn } from '@/lib/utils';

const WORKFLOW_ICONS = {
  'Report': PenLine,
  'Add Context': ListOrdered,
  'Review and Prioritise': SearchCheck,
  'Assign': UserCheck,
  'Track and Escalate': Eye,
  'Close and Learn': CheckCheck,
};

const EASE = [0.22, 1, 0.36, 1];

// Section 3 — from frontline report to closed action. Desktop: connected
// workflow with detail panel; mobile: vertical numbered sequence.
export const ForeseeWorkflow = ({ sp }) => {
  const steps = sp.workflow || [];
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();
  const current = steps[active] || steps[0];

  return (
    <section id="foresee-workflow" aria-labelledby="foresee-workflow-heading" data-testid="foresee-workflow" className="scroll-mt-28 bg-mist py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          index="03"
          eyebrow="How Foresee Works"
          title="Connect every report to accountable action"
          testid="foresee-workflow-header"
        />

        {/* Desktop: connected horizontal workflow */}
        <div className="mt-14 hidden md:block" data-testid="foresee-workflow-desktop">
          <ol className="relative mx-auto flex max-w-5xl items-start" role="list">
            <span className="absolute left-0 right-0 top-7 h-0.5 bg-slate-200" aria-hidden="true" />
            {steps.map((step, i) => {
              const Icon = WORKFLOW_ICONS[step.title] || PenLine;
              const isActive = i === active;
              return (
                <li key={step.title} className="relative flex flex-1 flex-col items-center px-1">
                  <button
                    type="button"
                    aria-pressed={isActive}
                    aria-controls="foresee-workflow-panel"
                    data-testid={`foresee-workflow-step-${i + 1}`}
                    onClick={() => setActive(i)}
                    className="group flex flex-col items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-2"
                  >
                    <span
                      className={cn(
                        'relative z-10 flex h-14 w-14 items-center justify-center rounded-full border-2 bg-white transition-[background-color,border-color,color,box-shadow] duration-300',
                        isActive
                          ? 'border-indigo-600 bg-indigo-600 text-white shadow-[0_0_0_6px_rgba(79,70,229,0.18)]'
                          : 'border-slate-300 text-slate-400 group-hover:border-indigo-300 group-hover:text-indigo-600'
                      )}
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className={cn('inline-flex max-w-[9rem] flex-col items-center gap-1 text-center text-xs font-extrabold tracking-tight transition-colors sm:text-sm', isActive ? 'text-navy-900' : 'text-slate-400 group-hover:text-navy-700')}>
                      <span className="font-mono text-[10px] font-medium tracking-widest text-indigo-500">{String(i + 1).padStart(2, '0')}</span>
                      {step.title}
                      {i < steps.length - 1 && <ArrowRight className="h-3 w-3 text-slate-300" aria-hidden="true" />}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
          <div id="foresee-workflow-panel" aria-live="polite" className="mx-auto mt-8 max-w-2xl">
            <AnimatePresence mode="wait">
              <motion.p
                key={current?.title}
                initial={reduce ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? undefined : { opacity: 0, y: -8 }}
                transition={{ duration: 0.3, ease: EASE }}
                className="rounded-xl border border-slate-200 bg-white px-6 py-5 text-center text-sm leading-relaxed text-slate-600 shadow-card sm:text-base"
                data-testid="foresee-workflow-detail"
              >
                <span className="font-bold text-navy-900">{current?.title}. </span>
                {current?.text}
              </motion.p>
            </AnimatePresence>
          </div>
          <p className="mt-5 text-center text-xs font-semibold text-slate-500">
            Reported issues never close themselves — every action keeps a clear owner and deadline until it is addressed.
          </p>
        </div>

        {/* Mobile: vertical sequence */}
        <ol className="relative mt-12 space-y-4 border-l-2 border-slate-200 pl-6 md:hidden" data-testid="foresee-workflow-mobile">
          {steps.map((step, i) => {
            const Icon = WORKFLOW_ICONS[step.title] || PenLine;
            return (
              <li key={step.title} className="relative" data-testid={`foresee-workflow-m-${i + 1}`}>
                <span className="absolute -left-[2.35rem] top-0 flex h-8 w-8 items-center justify-center rounded-full border-2 border-indigo-600 bg-indigo-600 text-white" aria-hidden="true">
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <h3 className="text-base font-extrabold tracking-tight text-navy-900">
                  <span className="mr-2 font-mono text-xs font-medium tracking-widest text-indigo-500">{String(i + 1).padStart(2, '0')}</span>
                  {step.title}
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">{step.text}</p>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
};
